import type { CSSProperties, ReactNode, Ref } from 'react';

import classNames from 'classnames';

import type { Color, ColorName } from '../../styles/color';
import { getColor } from '../../styles/color';
import type { ConstraintsProps } from '../../styles/constraints';
import { getConstraintsStyles } from '../../styles/constraints/getConstraintsStyles';
import type { SpacingProps } from '../../styles/spacing';
import { getSpacingStyles } from '../../styles/spacing';
import { Card } from '../Card';
import type { WaveProps } from '../Wave';
import { Wave } from '../Wave';

export type SectionVariant = 'wave' | 'card' | 'default';

/** The props for the {@link Section} component with `default` variant. */
export interface SectionPropsDefault {
  /** The section background color. */
  backgroundColor?: Color | ColorName;
}
/** The props for the {@link Section} component with `weave` variant. */
export interface SectionPropsWave {
  /**
   * Add a wave design element on the top.
   *
   * @defaultValue true
   */
  withTopWave?: WaveProps['withTop'];
  /**
   * Add a wave design element on the bottom.
   *
   * @defaultValue true
   */
  withBottomWave?: WaveProps['withBottom'];
  /** The fill color of the wave. */
  waveColor?: WaveProps['fill'];
  /** The fill color of the wave. */
  waveHeight?: WaveProps['waveHeight'];

  /**
   * The margin of the wave.
   *
   * @defaultValue 'margin-md'
   */
  waveMargin?: WaveProps['padding'];
}

/** The props for a {@link Section} with the "card" variant. */
export interface SectionPropsCard {
  /** The section background color. */
  backgroundColor?: Color | ColorName;
}

export function sectionIsDefaultVariant(
  props: SectionProps<SectionVariant, SectionAs>
): props is SectionProps<'default', SectionAs> {
  return !sectionIsWaveVariant(props) && !sectionIsCardVariant(props);
}

export function sectionIsCardVariant(
  props: SectionProps<SectionVariant, SectionAs>
): props is SectionProps<'card', SectionAs> {
  return props.variant === 'card';
}

export function sectionIsWaveVariant(
  props: SectionProps<SectionVariant, SectionAs>
): props is SectionProps<'wave', SectionAs> {
  return props.variant === 'wave';
}

/** The props for a {@link Section}. */
export type SectionProps<
  T extends SectionVariant,
  As extends SectionAs = 'section',
> = {
  /** The variant of the section. */
  variant?: T | 'default';
  /**
   * The element rendered by the section. Can be either `footer`, `header` or
   * `section` (default).
   */
  as?: As;
  /** An optional id of the section. */
  id?: string;
  /** The class name of the section. */
  className?: string;
  /** The style of the section. */
  style?: CSSProperties;
  /**
   * A ref to the outermost element. Avoided using `ref` here because this is a
   * generic component, which does not play nice with forwardRef
   */
  sectionRef?: Ref<HTMLElement>;
  children?: ReactNode;
} & SectionVariantsMap[T] &
  SectionAriaProps<As> &
  SpacingProps &
  ConstraintsProps;

/** The possible elements rendered by {@link Section}. */
export type SectionAs = 'div' | 'section' | 'header' | 'footer';

/** Map of the variants for the {@link Section} component. */

export type SectionVariantsMap = {
  wave: SectionPropsWave;
  card: SectionPropsCard;
  default: SectionPropsDefault;
};
/**
 * The ARIA props for the {@link Section} component.
 *
 * @see https://www.w3.org/WAI/ARIA/apg/practices/landmark-regions/#landmarkroles
 */
export type SectionAriaProps<As extends string | undefined = undefined> =
  As extends 'section'
    ? /** Makes either `aria-label` either `aria-labelledby` mandatory. */
        | {
            'aria-label': string;
            'aria-labelledby'?: never;
          }
        | {
            'aria-label'?: never;
            'aria-labelledby': string;
          }
    : /* in case it extends elements that are not a `region` role, neither aria-label or aria-labelledby are required */
      {
        'aria-label'?: string | undefined;
        'aria-labelledby'?: string | undefined;
      };

/**
 * Render a {@link Page} section (landmark with role "region") with different
 * styled variants.
 *
 * @deprecated Use {@link Box}, {@link Container}, {@link Wave} or {@link Card}
 *   instead.
 * @see https://corgi-x.tfd.engineering/components/section
 */
export function Section<
  T extends SectionVariant = 'default',
  A extends SectionAs = 'section',
>(props: SectionProps<T, A>) {
  const { as = 'section' } = props;

  const spacingStyles = getSpacingStyles('padding', props);
  const constraintsStyles = getConstraintsStyles(props);

  const Component = as;

  if (sectionIsWaveVariant(props)) {
    const {
      withTopWave = true,
      withBottomWave = true,
      waveMargin = 'margin-md',
    } = props;
    return (
      <Wave
        as={as}
        id={props.id}
        className={props.className}
        withTop={withTopWave}
        withBottom={withBottomWave}
        waveHeight={props.waveHeight}
        fill={props.waveColor}
        padding={waveMargin}
        aria-label={props['aria-label']}
        aria-labelledby={props['aria-labelledby']}
        ref={props.sectionRef}
      >
        <div className={classNames(constraintsStyles, spacingStyles)}>
          {props.children}
        </div>
      </Wave>
    );
  }

  if (sectionIsCardVariant(props)) {
    const backgroundColor = props.backgroundColor
      ? getColor(props.backgroundColor)
      : undefined;
    return (
      <div
        style={{ backgroundColor }}
        ref={props.sectionRef as Ref<HTMLDivElement>}
      >
        <div
          className={classNames(constraintsStyles, spacingStyles)}
          style={props.style}
        >
          <Card
            as={as}
            className={props.className}
            aria-label={props['aria-label']}
            aria-labelledby={props['aria-labelledby']}
          >
            {props.children}
          </Card>
        </div>
      </div>
    );
  }

  if (sectionIsDefaultVariant(props)) {
    const backgroundColor = props.backgroundColor
      ? getColor(props.backgroundColor)
      : undefined;

    return (
      <Component
        ref={props.sectionRef as Ref<HTMLDivElement>}
        id={props.id}
        className={props.className}
        style={{ ...props.style, backgroundColor }}
        aria-label={props['aria-label']}
        aria-labelledby={props['aria-labelledby']}
      >
        <div className={classNames(constraintsStyles, spacingStyles)}>
          {props.children}
        </div>
      </Component>
    );
  }

  return <></>;
}
